import 'BomdisoftComponents/row-entry/styles.css';
import { type RowEntryId, type RowEntryMemo, resolveRowEntry } from 'BomdisoftComponents/row-entry/detect';
import { ROW_ENTERED_CLASS } from 'BomdisoftComponents/row-entry/render';
import { useCallback, useEffect, useRef, useState } from 'react';

export { type RowEntry, type RowEntryId, type RowEntryMemo, resolveRowEntry } from 'BomdisoftComponents/row-entry/detect';
export { type RowPulseOptions, rowPulseAttributes } from 'BomdisoftComponents/row-entry/render';

export const rowEnteredClassName = ROW_ENTERED_CLASS;

const NOTHING: ReadonlySet<RowEntryId> = new Set();

const prefersReducedMotion = (): boolean => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

export type RowEntryHandle = {
  entered: ReadonlySet<RowEntryId>;
  /** Call from the row's `onAnimationEnd` so the mark does not outlive the pulse. */
  settle: (id: RowEntryId) => void;
};

/**
 * Marks the rows that just arrived, for as long as their pulse is running.
 *
 * `ids` MUST be referentially stable between renders that change nothing, since
 * it is the effect's trigger; pass a `useMemo` of the resolved row ids. An array
 * rebuilt every render would re-run the diff every render, and the second run
 * would find the arrival already remembered and clear the mark mid-animation.
 *
 * The mark is dropped on `animationend` rather than on a timer, so the duration
 * lives in CSS alone and cannot drift from a copy kept here.
 *
 * Which is exactly why reduced motion is checked HERE and not left to the
 * stylesheet: `animation: none` fires no `animationend`, so a marked row would
 * hold its mark forever and the set would grow without limit on the page this
 * rule was written for, a realtime feed that appends all day. A viewer who asked
 * for less motion is never marked in the first place.
 */
export const useRowEntry = (ids: readonly RowEntryId[], signature = ''): RowEntryHandle => {
  const [entered, setEntered] = useState<ReadonlySet<RowEntryId>>(NOTHING);
  const memo = useRef<RowEntryMemo | null>(null);

  useEffect(() => {
    const arrival = resolveRowEntry(ids, signature, memo.current);
    memo.current = arrival.memo;
    if (arrival.entered.size === 0 || prefersReducedMotion()) return;
    setEntered(previous => new Set([...previous, ...arrival.entered]));
  }, [ids, signature]);

  const settle = useCallback((id: RowEntryId) => {
    setEntered(previous => {
      if (!previous.has(id)) return previous;
      const next = new Set(previous);
      next.delete(id);
      return next.size === 0 ? NOTHING : next;
    });
  }, []);

  return { entered, settle };
};
